import { inject, Injectable } from '@angular/core'
import { _deepEquals } from '@naturalcycles/js-lib'
import { Experiment } from '@naturalcycles/shared'
import { exp312Quiz } from '@src/ab-312/quiz-modifier.js'
import { exp351Quiz } from '@src/ab-351/quiz-modifier.js'
import { exp355Quiz } from '@src/ab-355/quiz-modifier.js'
import { exp358Quiz } from '@src/ab-358/quiz-modifier.js'
import { exp369Quiz } from '@src/ab-369/quiz-modifier.js'
import { breakpointActions } from '@src/app/constants/breakpoints.js'
import { allQuizSections, commonQuizFlow, guardQuizData } from '@src/app/constants/quiz.js'
import { SignalStore } from '@src/app/core/store/signalStore.js'
import { flowUtils } from '@src/app/core/util/flow.util.js'
import { clearblueQuiz, metaLandingPageQuiz, QuizModifier } from '@src/app/core/util/quiz.util.js'
import { AnyPage, Page } from '@src/app/shared/typings/enum/pages.js'
import { Sections } from '@src/app/shared/typings/enum/quiz.js'
import {
  SignupQuizFlow,
  WSQuizEngine,
  WSQuizEngineConfig,
} from '@src/app/shared/typings/interfaces/quiz.js'
import { logger } from '../../util/log.util.js'

/**
 * GUIDE ON HOW TO ADD A NEW QUIZ FLOW:
 * (Works wether it's a new experiment or new variant clearblue-like flow)
 *
 * 1) Add new experiment/variant to the QuizSignatureObject
 *
 * After that, typescript will help you to find all the places where you need to update the code.
 *
 * 2) Add a new QuizModifier
 *
 * Each one of the QuizModifier methods should never replace entirely the input object, but rather mutate it.
 * This concept is key in order to keep flow/experiments non-mutually exclusive.
 *
 * 3) add new QuizModifier to the modifiers array in quiz service
 */
export interface QuizSignatureObject {
  experiment: {
    [Experiment.QUIZ_CHAPTER]: string | undefined
    [Experiment.QUIZ_NEEDS_SECTION]: string | undefined
    [Experiment.WS_CYCLE_INSIGHTS]: string | undefined
    [Experiment.EARLY_LEAD_CAPTURE]: string | undefined
    [Experiment.TYPICAL_USE]: string | undefined
  }
  isClearblueFlow: boolean
  isMetaLandingPageFlow: boolean
  isLoggedIn: boolean
}

@Injectable({ providedIn: 'root' })
export class QuizService {
  private store = inject(SignalStore)
  private engine?: WSQuizEngine

  private modifiers: QuizModifier<WSQuizEngineConfig>[] = [
    clearblueQuiz,
    exp312Quiz,
    exp351Quiz,
    exp355Quiz,
    metaLandingPageQuiz,
    exp358Quiz,
    exp369Quiz,
  ]

  private baseQuiz = {
    getEngineConfig(): WSQuizEngineConfig {
      return {
        breakpointActions,
        guardData: guardQuizData,
      }
    },
    getSections(): Sections {
      return allQuizSections
    },
    getInitialFlow(): SignupQuizFlow {
      return commonQuizFlow
    },
    getEntry(): AnyPage[] | null {
      // On default signup flow, there is no page before the first quiz page
      return null
    },
    getExit(): AnyPage[] | null {
      return [Page.measuringDevice]
    },
  }

  public isFirstInFlow(page: Page): boolean {
    return this.engine?.getFirstPageInFlow(this.store.$quiz().flow) === page
  }

  public init(so: QuizSignatureObject): void {
    logger.log('[QuizService] init')
    if (this.isQuizSignatureValid(so)) {
      logger.log('[QuizService] ... valid quiz signature')
      const cfg = this.getEngineConfig(so)
      this.engine = new WSQuizEngine(cfg)
      return
    }

    logger.log('[QuizService] ...invalid quiz signature. Rebuilding engine with new signature')
    this.bootstrapEngine(so)

    logger.log('[QuizService] ... init done')
  }

  private bootstrapEngine(so: QuizSignatureObject): void {
    const cfg = this.getEngineConfig(so)
    this.engine = new WSQuizEngine(cfg)

    this.store.clearQuizState()

    this.store.replaceQuizState({
      data: null,
      flow: this.getInitialFlow(so),
      sections: this.getQuizSections(so),
      exit: this.getQuizExit(so),
      entry: this.getQuizEntry(so),
      signature: so,
    })
    this.store.persistToLocalStorage()
  }

  public validateQuizSignature(so: QuizSignatureObject): void {
    logger.log('[QuizService] validate quiz signature')
    if (this.isQuizSignatureValid(so)) {
      logger.log('[QuizService] ... valid quiz signature')
      return
    }

    logger.log('[QuizService] ...invalid quiz signature. Rebuilding engine with new signature')
    this.bootstrapEngine(so)
  }

  public reboot(so: QuizSignatureObject): void {
    logger.log('[QuizService] reboot')
    this.bootstrapEngine(so)
  }

  public getEngine(): WSQuizEngine {
    if (!this.engine) {
      throw new Error('[QuizService] Must initialize quiz engine before accessing it')
    }

    return this.engine
  }

  private getEngineConfig(obj: QuizSignatureObject): WSQuizEngineConfig {
    let config = this.baseQuiz.getEngineConfig()
    this.modifiers.forEach(modifier => {
      config = modifier.enhanceEngineConfig(obj, config)
    })
    return config
  }

  private getInitialFlow(obj: QuizSignatureObject): SignupQuizFlow {
    let initialFlow = this.baseQuiz.getInitialFlow()
    this.modifiers.forEach(modifier => {
      initialFlow = modifier.enhanceInitialFlow(obj, initialFlow)
    })
    return initialFlow
  }

  private getQuizSections(obj: QuizSignatureObject): Sections {
    let sections = this.baseQuiz.getSections()
    this.modifiers.forEach(modifier => {
      sections = modifier.enhanceSections(obj, sections)
    })
    return sections
  }

  private getQuizEntry(obj: QuizSignatureObject): AnyPage[] | null {
    let entry = this.baseQuiz.getEntry()
    this.modifiers.forEach(modifier => {
      entry = modifier.enhanceEntry(obj, entry)
    })
    return entry
  }

  private getQuizExit(obj: QuizSignatureObject): AnyPage[] | null {
    let exit = this.baseQuiz.getExit()
    this.modifiers.forEach(modifier => {
      exit = modifier.enhanceExit(obj, exit)
    })
    return exit
  }

  private isQuizSignatureValid(so: QuizSignatureObject): boolean {
    const storedSignature = this.store.$quiz().signature
    const { experiment, isLoggedIn, ...rest } = so
    const {
      experiment: storedExperiment,
      isLoggedIn: storedIsLoggedIn,
      ...storedRest
    } = storedSignature || ({} as Partial<QuizSignatureObject>)
    if (isLoggedIn && !storedIsLoggedIn) {
      // We get different experiments state when we log in,
      // so we need to ignore the experiment part of the signature
      // when the user has just logged in
      if (storedSignature) {
        this.store.$quiz.set({
          ...this.store.$quiz(),
          signature: {
            ...storedSignature,
            experiment,
            isLoggedIn,
          },
        })
      }
      return _deepEquals(rest, storedRest)
    }
    return _deepEquals(so, storedSignature)
  }

  public isLastPage(page: Page | null): boolean {
    const quiz = this.store.$quiz()
    return this.getEngine().getLastPageInFlow(quiz.flow) === page
  }

  public getSignatureObject(): QuizSignatureObject {
    const state = this.store.getState()
    const {
      experiment: { assignments },
    } = state

    const params = Object.fromEntries(new URLSearchParams(location.search).entries())

    return {
      experiment: {
        [Experiment.QUIZ_CHAPTER]: assignments[Experiment.QUIZ_CHAPTER]?.bucket,
        [Experiment.QUIZ_NEEDS_SECTION]: assignments[Experiment.QUIZ_NEEDS_SECTION]?.bucket,
        [Experiment.WS_CYCLE_INSIGHTS]: assignments[Experiment.WS_CYCLE_INSIGHTS]?.bucket,
        [Experiment.EARLY_LEAD_CAPTURE]: assignments[Experiment.EARLY_LEAD_CAPTURE]?.bucket,
        [Experiment.TYPICAL_USE]: assignments[Experiment.TYPICAL_USE]?.bucket,
      },
      isClearblueFlow: flowUtils.hasCBCode({ state, params }),
      isMetaLandingPageFlow: flowUtils.isMetaLandingPageFlow({ state, params }),
      isLoggedIn: flowUtils.isLoggedIn({ state, params }),
    }
  }
}
/*
Q: Why do some pages have URLs unique for both "prevent" and "plan" flows, while others have separate
/xxx-prevent" and "/xxx-plan" URLs even if they "look" the same?
A: Different URLs means that when the user lands on the page (Eg. by direct link) we know
   immediately his position in the flow, without the necessity to look into the state data to
   discern if user is in "prevent" or "plan".


Q: Why do we have "info-pages" which are shared between "prevent" and "plan" flows?
A: It seemed overkill to have many duplicates of the same page in the case of "info-pages". Since their
role is strictly presentational (no BreakpointAction needs to be processed), we allow them to be
shared between "prevent" and "plan" flows when needed.


Q: Can I expand the functionality?
A: Sure! As long as the service stays:
- free from side effects
- free from interaction with Angular features (store, route, etc)

This helps isolating the service so that it is more easily unit testable.
 */
